// Évaluations — objectifs SMART : cartes, check-ins trimestriels,
// guide (bon/mauvais + exemples par niveau), formulaire, et le board
// équipe agrégé pour le manager. Ported from ev-objectifs.jsx.
import { useState } from 'react';
import {
  EVAL_GUIDE_BVM, EVAL_GUIDE_LEVELS, EVAL_OBJ_CATS, EVAL_QDATES, EVAL_QUARTERS,
  OBJECTIVE_STATUS_LBL,
  type EvalEmployeeDTO, type EvalQuarter, type ObjectiveCheckStatus, type ObjectiveDTO, type ObjectiveTemplate,
} from '@pointage/shared';
import { EvEmpCell } from './common.js';
import { useUnsavedGuard } from '../../lib/unsaved.js';

const statusColor = (s: ObjectiveCheckStatus) =>
  s === 'ontrack' ? 'var(--green)' : s === 'risk' ? 'var(--warn)' : 'var(--bad)';

const lastCheck = (o: ObjectiveDTO) =>
  (['T4', 'T3', 'T2', 'T1'] as EvalQuarter[]).map((q) => o.checks[q]).find(Boolean);

export interface ObjCheckInput {
  q: EvalQuarter;
  prog: number;
  status: ObjectiveCheckStatus;
  note: string;
}

// ─── carte objectif ──────────────────────────────────────────
export function EvObjCard({ obj, persona, empName, mgrName, onValidate, onRefuse, onCheck, delay }: {
  obj: ObjectiveDTO;
  persona: 'mgr' | 'emp';
  empName: string;
  mgrName: string;
  onValidate?: () => void;
  onRefuse?: () => void;
  onCheck?: (input: ObjCheckInput) => void;
  delay: number;
}) {
  const [open, setOpen] = useState<EvalQuarter | null>(null);
  const [edit, setEdit] = useState<{ q: EvalQuarter; prog: number; status: ObjectiveCheckStatus; note: string } | null>(null);
  useUnsavedGuard({
    id: `objectif-check-${obj.id}`,
    dirty: !!edit,
    label: 'Évaluations · check-in d’objectif',
    summary: obj.title,
    save: edit && onCheck ? async () => { onCheck(edit); setEdit(null); } : undefined,
    discard: () => setEdit(null),
  });
  const last = lastCheck(obj);
  const prog = last?.prog ?? 0;
  const status: ObjectiveCheckStatus = last?.status ?? 'ontrack';
  const nextQ = EVAL_QUARTERS.find((q) => !obj.checks[q]) ?? 'T4';
  const propose = obj.status === 'PROPOSE';
  return (
    <div className="ev-obj" style={{ animationDelay: `${delay * 0.04}s`, ...(propose ? { borderStyle: 'dashed', borderColor: 'var(--shf)' } : {}) }}>
      <div className="cat">{obj.cat}</div>
      {propose && <span className="ev-st propose" style={{ color: 'var(--shf)', background: 'var(--shfs)' }}><i />Proposition</span>}
      <h4>{obj.title}</h4>
      {obj.description && (
        <div style={{ fontSize: 11.5, color: 'var(--ink3)', lineHeight: 1.5, marginTop: -3 }}>{obj.description}</div>
      )}
      <div className="kpi">{obj.kpi}</div>
      <div className="meta">
        <span>⏱ {obj.deadline}</span>
        {!propose && (<>
          <span>·</span>
          <span style={{ color: statusColor(status), fontWeight: 800 }}>{OBJECTIVE_STATUS_LBL[status]}</span>
          <span style={{ marginLeft: 'auto', fontFamily: "'JetBrains Mono',monospace" }}>{prog}%</span>
        </>)}
      </div>
      {!propose && <div className="ev-objbar"><i style={{ width: `${prog}%`, background: statusColor(status) }} /></div>}
      {!propose && (
        <div className="ev-checks">
          {EVAL_QUARTERS.map((q) => {
            const c = obj.checks[q];
            return (
              <button key={q}
                className={'ev-checkchip ' + (c ? (c.status === 'ontrack' ? 'ok' : c.status) : '') + (open === q ? ' open' : '')}
                onClick={() => c
                  ? setOpen(open === q ? null : q)
                  : (q === nextQ && onCheck ? setEdit({ q, prog: Math.min(100, prog + 20), status, note: '' }) : undefined)}
                title={c ? `${EVAL_QDATES[q]} · ${c.prog}%` : q === nextQ && onCheck ? `Saisir le check-in ${q}` : `À venir · ${EVAL_QDATES[q]}`}>
                {q}<b>{c ? `${c.prog}%` : q === nextQ && onCheck ? '+' : '·'}</b>
              </button>
            );
          })}
        </div>
      )}
      {open && obj.checks[open] && (
        <div className="ev-checkdetail">
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 11 }}>
            <span>Check-in {open} · {EVAL_QDATES[open]}</span>
            <span style={{ color: statusColor(obj.checks[open]!.status) }}>{OBJECTIVE_STATUS_LBL[obj.checks[open]!.status]} · {obj.checks[open]!.prog}%</span>
          </div>
          <div className="by">{empName}</div><p>{obj.checks[open]!.emp || '—'}</p>
          <div className="by">{mgrName}</div><p>{obj.checks[open]!.mgr || '—'}</p>
        </div>
      )}
      {edit && (
        <div className="ev-checkdetail">
          <div style={{ fontWeight: 800, fontSize: 11, marginBottom: 8 }}>Check-in {edit.q} — avancement</div>
          <input type="range" className="ev-range" min={0} max={100} step={5} value={edit.prog}
            onChange={(e) => setEdit({ ...edit, prog: Number(e.target.value) })} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>
            <span>0%</span><b>{edit.prog}%</b><span>100%</span>
          </div>
          <div className="ev-statuspick" style={{ marginBottom: 8 }}>
            {(['ontrack', 'risk', 'off'] as ObjectiveCheckStatus[]).map((s) => (
              <button key={s} className={edit.status === s ? (s === 'ontrack' ? 'ok' : s) : ''}
                onClick={() => setEdit({ ...edit, status: s })}>{OBJECTIVE_STATUS_LBL[s]}</button>
            ))}
          </div>
          <textarea className="ev-ta" style={{ minHeight: 52 }}
            placeholder={persona === 'mgr' ? 'Commentaire manager…' : 'Votre commentaire…'}
            value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
          <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
            <button className="gm-attn-go" style={{ padding: '8px 14px', fontSize: 11.5 }}
              onClick={() => { onCheck?.(edit); setEdit(null); }}>Enregistrer le check-in</button>
            <button className="cg-ghost" style={{ padding: '7px 12px', fontSize: 11.5 }} onClick={() => setEdit(null)}>Annuler</button>
          </div>
        </div>
      )}
      {propose && persona === 'mgr' && (
        <div className="ev-obj-actions">
          <button className="gm-attn-go" style={{ padding: '8px 14px', fontSize: 11.5 }} onClick={onValidate}>✓ Valider l'objectif</button>
          <button className="cg-ghost" style={{ padding: '7px 12px', fontSize: 11.5 }} onClick={onRefuse}>Refuser</button>
        </div>
      )}
      {propose && persona === 'emp' && (
        <div style={{ fontSize: 11, color: 'var(--ink4)', fontWeight: 600 }}>En attente de validation par {mgrName}…</div>
      )}
    </div>
  );
}

// ─── section objectifs (un employé) ──────────────────────────
export function EvObjSection({ objs, persona, empName, mgrName, onCreate, onValidate, onRefuse, onCheck }: {
  objs: ObjectiveDTO[];
  persona: 'mgr' | 'emp';
  empName: string;
  mgrName: string;
  onCreate: (tpl: ObjectiveTemplate & { description?: string }) => void;
  onValidate?: (id: string) => void;
  onRefuse?: (id: string) => void;
  onCheck?: (id: string, input: ObjCheckInput) => void;
}) {
  const [guide, setGuide] = useState(false);
  const [form, setForm] = useState<(ObjectiveTemplate & { description?: string }) | null>(null);
  const valides = objs.filter((o) => o.status === 'VALIDE');
  const props = objs.filter((o) => o.status === 'PROPOSE');
  const risk = valides.filter((o) => { const l = lastCheck(o); return l && l.status !== 'ontrack'; }).length;
  const openForm = (preset?: ObjectiveTemplate) =>
    setForm(preset ?? { cat: EVAL_OBJ_CATS[0], title: '', kpi: '', deadline: 'Décembre 2026' });
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span className="ev-st" style={{ color: 'var(--ink2)', background: 'var(--s2)' }}>
          <i />{valides.length} objectif{valides.length > 1 ? 's' : ''} validé{valides.length > 1 ? 's' : ''}
        </span>
        {risk > 0 && <span className="ev-st" style={{ color: 'var(--warnc)', background: 'var(--warns)' }}><i />{risk} à risque</span>}
        {props.length > 0 && <span className="ev-st" style={{ color: 'var(--shf)', background: 'var(--shfs)' }}><i />{props.length} proposition{props.length > 1 ? 's' : ''} en attente</span>}
        <span style={{ flex: 1 }} />
        <button className="cg-ghost" style={{ whiteSpace: 'nowrap' }} onClick={() => setGuide(true)}>📖 Guide SMART</button>
        <button className="gm-attn-go" onClick={() => openForm()}>
          {persona === 'emp' ? '+ Proposer un objectif' : '+ Ajouter un objectif'}
        </button>
      </div>
      <div className="ev-objgrid">
        {props.map((o, i) => (
          <EvObjCard key={o.id} obj={o} persona={persona} empName={empName} mgrName={mgrName} delay={i}
            onValidate={onValidate ? () => onValidate(o.id) : undefined}
            onRefuse={onRefuse ? () => onRefuse(o.id) : undefined} />
        ))}
        {valides.map((o, i) => (
          <EvObjCard key={o.id} obj={o} persona={persona} empName={empName} mgrName={mgrName} delay={props.length + i}
            onCheck={onCheck ? (input) => onCheck(o.id, input) : undefined} />
        ))}
      </div>
      {objs.length === 0 && (
        <div className="ev-empty"><span className="big">🎯</span>Aucun objectif pour l'instant — proposez-en depuis le guide SMART.</div>
      )}

      {guide && (<>
        <div className="ev-ov" onClick={() => setGuide(false)} />
        <div className="ev-guide">
          <div className="ev-panel-h">
            <div>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Guide des objectifs — exemples par niveau</div>
              <div style={{ fontSize: 11, color: 'var(--ink3)' }}>Chaque objectif est SMART : Spécifique, Mesurable, Atteignable, Réaliste, Temporel.</div>
            </div>
            <button className="sb-dr-x x" onClick={() => setGuide(false)}>✕</button>
          </div>
          <div className="ev-guide-b">
            <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink4)', marginBottom: 8 }}>
              Bon objectif vs mauvais objectif
            </div>
            {EVAL_GUIDE_BVM.map((p, i) => (
              <div key={i} className="ev-bvm">
                <div className="b"><span className="tag">✕ Vague</span>{p[0]}</div>
                <div className="g"><span className="tag">✓ SMART</span>{p[1]}</div>
              </div>
            ))}
            {Object.entries(EVAL_GUIDE_LEVELS).map(([lvl, items]) => (
              <div key={lvl}>
                <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink4)', margin: '18px 0 8px' }}>
                  Exemples — {lvl}
                </div>
                {items.map((x, i) => (
                  <div key={i} className="ev-gx" onClick={() => { setGuide(false); openForm({ ...x }); }}>
                    <span className="use">Utiliser →</span>
                    <div className="c">{x.cat}</div>
                    <p>{x.title}</p>
                    <div className="k">KPI : {x.kpi} · {x.deadline}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </>)}

      {form && (
        <div className="ev-modal-ov" onClick={(e) => e.target === e.currentTarget && setForm(null)}>
          <div className="ev-modal">
            <div className="ev-modal-h">
              <h3>{persona === 'emp' ? 'Proposer un objectif' : 'Ajouter un objectif'} — {empName}</h3>
              <button className="sb-dr-x" onClick={() => setForm(null)}>✕</button>
            </div>
            <div className="ev-modal-b">
              <div className="ev-field">
                <label>Catégorie</label>
                <select value={form.cat} onChange={(e) => setForm({ ...form, cat: e.target.value })}>
                  {EVAL_OBJ_CATS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="ev-field">
                <label>Objectif (formulation SMART)</label>
                <textarea className="ev-ta" style={{ minHeight: 62 }} value={form.title}
                  placeholder="Ex. Générer au moins 2 opportunités d'upsell présentées au client d'ici décembre 2026"
                  onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="ev-field">
                <label>Description / actions (optionnel)</label>
                <textarea className="ev-ta" style={{ minHeight: 48 }} value={form.description ?? ''}
                  placeholder="Comment cet objectif sera atteint — actions, moyens, étapes…"
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="ev-f2">
                <div className="ev-field">
                  <label>KPI / indicateur</label>
                  <input value={form.kpi} placeholder="Ex. ≥ 2 opportunités présentées" onChange={(e) => setForm({ ...form, kpi: e.target.value })} />
                </div>
                <div className="ev-field">
                  <label>Deadline</label>
                  <input value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
                </div>
              </div>
              <div className="ev-gap-note">
                💡 Évitez les formulations vagues (« améliorer mes compétences ») — visez un résultat vérifiable,
                lié à un KPI et une échéance. Le guide SMART propose des exemples prêts à l'emploi.
              </div>
              <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end', marginTop: 6 }}>
                <button className="cg-ghost" onClick={() => setForm(null)}>Annuler</button>
                <button className="gm-attn-go" disabled={!form.title.trim() || !form.kpi.trim()}
                  onClick={() => { onCreate(form); setForm(null); }}>
                  {persona === 'emp' ? 'Envoyer la proposition' : "Valider l'objectif"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── board équipe (manager) ──────────────────────────────────
export function EvObjBoard({ team, objectives, onOpen }: {
  team: EvalEmployeeDTO[];
  objectives: Record<string, ObjectiveDTO[]>;
  onOpen: (emp: EvalEmployeeDTO, tab: string) => void;
}) {
  const rows = team.map((e) => {
    const objs = objectives[e.id] ?? [];
    const val = objs.filter((o) => o.status === 'VALIDE');
    const stat = { ontrack: 0, risk: 0, off: 0 };
    let prog = 0;
    for (const o of val) {
      const l = lastCheck(o);
      if (l) { stat[l.status]++; prog += l.prog; }
    }
    return { e, val, stat, avg: val.length ? Math.round(prog / val.length) : 0, props: objs.filter((o) => o.status === 'PROPOSE').length };
  }).sort((a, b) => (b.stat.off * 2 + b.stat.risk) - (a.stat.off * 2 + a.stat.risk));
  return (
    <div className="gm-gridwrap">
      <div className="gm-scroll">
        <table className="sb-tbl">
          <thead>
            <tr><th>Employé</th><th>Objectifs</th><th>Avancement moyen</th><th>T1</th><th>T2</th><th>Statuts</th><th>Propositions</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.e.id} className="cg-row" style={{ animationDelay: `${i * 0.02}s` }} onClick={() => onOpen(r.e, 'objectifs')}>
                <td><EvEmpCell emp={r.e} /></td>
                <td className="gm-mono" style={{ fontSize: 12 }}>{r.val.length}</td>
                <td style={{ minWidth: 150 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div className="ev-objbar" style={{ flex: 1 }}>
                      <i style={{ width: `${r.avg}%`, background: r.stat.off ? 'var(--bad)' : r.stat.risk ? 'var(--warn)' : 'var(--green)' }} />
                    </div>
                    <span className="gm-mono" style={{ fontSize: 11 }}>{r.avg}%</span>
                  </div>
                </td>
                {(['T1', 'T2'] as EvalQuarter[]).map((q) => (
                  <td key={q}>
                    <div className="ev-mini">
                      {r.val.map((o) => {
                        const c = o.checks[q];
                        return <b key={o.id}
                          className={c ? (c.status === 'ontrack' ? 'ok' : c.status === 'risk' ? 'mid' : '') : ''}
                          style={c && c.status === 'off' ? { background: 'var(--bad)' } : undefined}
                          title={c ? `${o.title} — ${c.prog}%` : 'pas de check-in'} />;
                      })}
                    </div>
                  </td>
                ))}
                <td>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {r.stat.ontrack > 0 && <span className="ev-st" style={{ color: 'var(--greenc)', background: 'var(--greens)' }}><i />{r.stat.ontrack}</span>}
                    {r.stat.risk > 0 && <span className="ev-st" style={{ color: 'var(--warnc)', background: 'var(--warns)' }}><i />{r.stat.risk}</span>}
                    {r.stat.off > 0 && <span className="ev-st" style={{ color: 'var(--badc)', background: 'var(--bads)' }}><i />{r.stat.off}</span>}
                  </div>
                </td>
                <td>
                  {r.props > 0
                    ? <span className="ev-st" style={{ color: 'var(--shf)', background: 'var(--shfs)' }}><i />{r.props} à valider</span>
                    : <span style={{ color: 'var(--ink4)', fontSize: 11 }}>—</span>}
                </td>
                <td style={{ textAlign: 'right', color: 'var(--ink4)' }}>›</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
